import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { matchPath } from 'react-router-dom';
import Resumable from 'resumablejs';
import styled from 'styled-components';
import ErrorPageBoundary from './components/pages/_error';
import './index.css';
import AdministratorLayout from './layouts/administratorLayout';
import AuthLayout from './layouts/authLayout';
import DefaultLayout from './layouts/defaultLayout';
import GuestLayout from './layouts/guestLayout';
import PrintOSLogin from './printOSLogin';
import { Relay } from './relay';
import administratorRoutes from './shared/administratorRoutes';
import config from './shared/config';
import { DrawerProvider } from './components/widgets/drawer';
import CustomThemeProvider from './shared/customThemeProvider';
import { FeatureFlagsProvider } from './shared/feature-flags';
import { backofficeUrl } from './shared/helpers';
import routes from './shared/routes';
import routesToLogin from './shared/routesToLogin';

// 🌐 Setup globale di Resumable per il 3D Core
// Il 3D Core cerca window.Resumable durante l'export AR/USDZ
declare global {
    interface Window {
        Resumable: any;
        updateResumableToken: (token: string) => void;
        getResumableConfig: () => any;
    }
}

// Configurazione globale per Resumable con token dinamico
let globalResumableConfig = {
    token: '',
    imagesUploadApiUrl: ''
};

// Funzione per aggiornare il token globalmente
const updateResumableToken = (token: string, imagesUploadApiUrl?: string) => {
    globalResumableConfig.token = token;
    if (imagesUploadApiUrl) {
        globalResumableConfig.imagesUploadApiUrl = imagesUploadApiUrl;
    }
};

// Funzione per ottenere la configurazione Resumable
const getResumableConfig = () => globalResumableConfig;

// Wrapper di Resumable che include automaticamente il token globale
// Funzione costruttore che può essere chiamata con 'new'
function ResumableWithToken(this: any, options: any) {
    // Leggi la configurazione globale al momento della creazione (non cachata)
    const config = getResumableConfig();

    // Fallback al config locale se quello globale è vuoto
    const finalToken = config.token || (window as any).config?.token || '';

    // Merge delle opzioni con la configurazione globale
    const finalOptions = {
        ...options,
        headers: {
            ...options.headers,
            Authorization: `Bearer ${finalToken}`
        }
    };

    // Crea una nuova istanza di Resumable e copia tutte le proprietà su 'this'
    const resumableInstance = new Resumable(finalOptions);

    // Se chiamato con 'new', copia tutte le proprietà dell'istanza su 'this'
    if (this instanceof ResumableWithToken) {
        Object.setPrototypeOf(this, Object.getPrototypeOf(resumableInstance));
        Object.assign(this, resumableInstance);
        return this;
    }

    // Se chiamato come funzione normale, restituisce l'istanza
    return resumableInstance;
}

// Espone Resumable e funzioni di configurazione globalmente per il 3D Core
if (typeof window !== 'undefined') {
    // Espone sia la funzione costruttore wrapper che la classe originale
    (window as any).Resumable = ResumableWithToken;
    (window as any).OriginalResumable = Resumable; // Backup della classe originale
    (window as any).updateResumableToken = updateResumableToken;
    (window as any).getResumableConfig = getResumableConfig;

    // Espone il config locale come fallback per il token
    (window as any).config = config;
}

export const Center = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
`;

const root = document.createElement('div');
root.setAttribute('id', 'root');
document.body.prepend(root);

const currentPath = window.location.pathname;
let queryString = window.location.search;

const regexCultureAdmin = /^\/[^/]+\/[Aa]dmin/; // Corrisponde a /culture/Admin
const regexAdmin = /^\/{0,2}[Aa]dmin/; // Corrisponde a /Admin
const regexCulture = /^\/[a-z]{2}(-[a-z]{2})?\//i; // Corrisponde a /en/ o /en-US/

const pathsToExclude = ['pricing', 'PartnerProgramContact', 'support'];

const pathName = window.location.pathname;
const currentRoute = routes.find((x) => {
    const mathWithParams = matchPath(x.url.replaceAll('?', ''), pathName);

    // Remove all optional params from x.url
    const urlWithNoOptionalParams = x.url.replace(/:\w+\?/g, '');

    const matchNoParams = matchPath(urlWithNoOptionalParams, pathName + '/');

    return matchNoParams || mathWithParams;
});

if (
    pathsToExclude.every((x) => !currentPath.toLowerCase().includes(x.toLocaleLowerCase())) &&
    !regexCultureAdmin.test(currentPath) &&
    !regexAdmin.test(currentPath)
) {
    window.location.href = `${backofficeUrl(currentPath)}${queryString}`;
} else if (pathsToExclude.every((x) => !currentPath.toLowerCase().includes(x)) && regexAdmin.test(currentPath)) {
    // Remove double slashes
    const newPath = currentPath.replace(/\/{2,}/g, '/');
    window.location.href = `/${config.culture}${newPath}${queryString}`;
} else {
    // Se il path è in pathsToExclude ma non ha la cultura, aggiungila e redirect
    if (pathsToExclude.some((x) => currentPath.toLowerCase().includes(x.toLowerCase())) && !regexCulture.test(currentPath)) {
        window.location.href = `/${config.culture}${currentPath}${queryString}`;
    } else {
        // Check if we are in any login page, skip / and '' routes
        const isAuthLayout = routesToLogin.some((x, idx) => idx > 1 && currentPath.toLowerCase().startsWith(backofficeUrl(x.url).toLowerCase()));

        const isAdministratorLayout = administratorRoutes.some((x) => currentPath.toLowerCase().startsWith(backofficeUrl(x.url).toLowerCase()));

        const layoutToRender = isAuthLayout ? (
            <AuthLayout />
        ) : currentRoute?.isGuestAllowed ? (
            <GuestLayout />
        ) : isAdministratorLayout ? (
            <AdministratorLayout />
        ) : (
            <DefaultLayout />
        );

        ReactDOM.createRoot(root).render(
            <ErrorPageBoundary>
                <Relay>
                    <FeatureFlagsProvider>
                        <Suspense fallback={null}>
                            <PrintOSLogin>
                                <CustomThemeProvider>
                                    <DrawerProvider>{layoutToRender}</DrawerProvider>
                                </CustomThemeProvider>
                            </PrintOSLogin>
                        </Suspense>
                    </FeatureFlagsProvider>
                </Relay>
            </ErrorPageBoundary>
        );
    }
}
